/**
 * Types pour la gestion hiérarchique des unités commerciales
 * Permet le filtrage des données à 3 niveaux :
 * - Entreprise (niveau global, par défaut) - niveau 0
 * - Succursale (filiale/branche) - niveau 1
 * - Point de vente (niveau le plus granulaire) - niveau 2
 * 
 * Aligné avec la documentation API: /API DOCUMENTATION/organization.md
 */

/**
 * Types d'unité commerciale (enum)
 */
export type BusinessUnitType = 'company' | 'branch' | 'pos';

/**
 * Statuts d'unité commerciale
 */
export type BusinessUnitStatus = 'active' | 'inactive' | 'suspended' | 'closed';

/**
 * Labels des types d'unité commerciale
 */
export const BUSINESS_UNIT_TYPE_LABELS: Record<BusinessUnitType, string> = {
  company: 'Entreprise',
  branch: 'Succursale',
  pos: 'Point de vente'
};

/**
 * Labels des statuts d'unité commerciale
 */
export const BUSINESS_UNIT_STATUS_LABELS: Record<BusinessUnitStatus, string> = {
  active: 'Actif',
  inactive: 'Inactif',
  suspended: 'Suspendu',
  closed: 'Fermé'
};

/**
 * Paramètres spécifiques d'une unité commerciale
 */
export interface BusinessUnitSettings {
  defaultJournalTypes?: string[];       // Types de journaux autorisés
  allowedTransactionTypes?: string[];   // Types de transactions autorisées
  maxTransactionAmount?: number;        // Montant max par transaction
  dailyTransactionLimit?: number;       // Limite quotidienne
  requireApprovalAbove?: number;        // Seuil d'approbation requise
  approvalWorkflow?: string[];          // Workflow d'approbation
}

/**
 * Interface de base pour une unité commerciale
 * Alignée avec l'API: GET /business-units/:id
 */
export interface BusinessUnit {
  id: string;
  code: string;                         // Code unique (ex: BRN-001, POS-KIN-001)
  name: string;                         // Nom de l'unité
  type: BusinessUnitType;               // Type: company, branch, pos
  status: BusinessUnitStatus;           // Statut de l'unité
  companyId: string;                    // ID de l'entreprise principale
  parentId?: string;                    // ID du parent (null pour company)
  hierarchyLevel: number;               // Niveau hiérarchique (0=company, 1=branch, 2=pos)
  hierarchyPath?: string;               // Chemin hiérarchique (ex: "/company-id/branch-id")
  address?: string;
  city?: string;
  province?: string;
  country: string;                      // Pays (défaut: RDC)
  phone?: string;
  email?: string;
  manager?: string;                     // Nom du responsable
  managerId?: string;                   // ID utilisateur du responsable
  currency: string;                     // Devise (défaut: CDF)
  timezone: string;                     // Fuseau horaire (défaut: Africa/Kinshasa)
  settings?: BusinessUnitSettings;      // Paramètres spécifiques
  metadata?: Record<string, unknown>;   // Métadonnées additionnelles
  createdAt: string;
  updatedAt: string;
  createdBy?: string;                   // ID utilisateur créateur
  updatedBy?: string;                   // ID utilisateur dernière modification
  
  // Propriété dépréciée - utiliser status à la place
  /** @deprecated Utiliser status === 'active' à la place */
  isActive?: boolean;
}

/**
 * Interface spécifique pour une entreprise (niveau racine)
 */
export interface Company extends BusinessUnit {
  type: 'company';
  hierarchyLevel: 0;
  registrationNumber?: string;
  taxId?: string;
  legalForm?: string;
  branches?: Branch[];
}

/**
 * Interface spécifique pour une succursale
 */
export interface Branch extends BusinessUnit {
  type: 'branch';
  hierarchyLevel: 1;
  pointsOfSale?: PointOfSale[];
}

/**
 * Interface spécifique pour un point de vente
 * Un POS peut être rattaché à une succursale OU directement à l'entreprise
 */
export interface PointOfSale extends BusinessUnit {
  type: 'pos';
  hierarchyLevel: 2;
  branchId?: string; // ID de la succursale parente (optionnel - peut être directement sous l'entreprise)
  posType?: 'shop' | 'warehouse' | 'kiosk' | 'online' | 'other';
}

/**
 * Contexte de sélection d'unité commerciale pour les filtres
 */
export interface BusinessUnitContext {
  level: BusinessUnitType;
  companyId: string; // Toujours renseigné
  branchId?: string; // Renseigné si niveau branch ou pos
  posId?: string; // Renseigné si niveau pos
}

/**
 * Filtre basé sur l'unité commerciale (simple)
 */
export interface BusinessUnitFilter {
  businessUnitId?: string;
  businessUnitType?: BusinessUnitType;
  includeChildren?: boolean; // Inclure les données des sous-unités
}

/**
 * DTO pour les filtres de recherche (aligné avec l'API)
 * Utilisé pour: GET /business-units
 */
export interface BusinessUnitFilterDto {
  page?: number;                    // Numéro de page (défaut: 1)
  pageSize?: number;                // Éléments par page (défaut: 20, max: 100)
  type?: BusinessUnitType;          // Filtrer par type
  status?: BusinessUnitStatus;      // Filtrer par statut
  parentId?: string;                // Filtrer par parent
  search?: string;                  // Recherche sur nom ou code
  city?: string;                    // Filtrer par ville
  province?: string;                // Filtrer par province
  includeChildren?: boolean;        // Inclure les enfants (défaut: false)
}

/**
 * DTO pour les données agrégées (aligné avec l'API)
 * Utilisé pour: GET /business-units/:id/aggregate
 */
export interface BusinessUnitAggregateDto {
  id: string;
  name: string;
  type: BusinessUnitType;
  totalRevenue: number;             // Total des revenus
  totalExpenses: number;            // Total des dépenses
  netIncome: number;                // Résultat net
  totalAssets: number;              // Total des actifs
  totalLiabilities: number;         // Total des passifs
  cashBalance: number;              // Solde de trésorerie
  transactionCount: number;         // Nombre de transactions
  periodStart: string;              // Début de période
  periodEnd: string;                // Fin de période
  children?: BusinessUnitAggregateDto[];  // Ventilation par enfant
}

/**
 * DTO pour la hiérarchie (aligné avec l'API)
 * Utilisé pour: GET /business-units/hierarchy
 */
export interface BusinessUnitHierarchyDto {
  id: string;
  code: string;
  name: string;
  type: BusinessUnitType;
  status: BusinessUnitStatus;
  level: number;
  children?: BusinessUnitHierarchyDto[];
  stats?: {
    childCount: number;
    activeChildCount: number;
    transactionCount: number;
    totalBalance: number;
  };
}

/**
 * DTO pour les statistiques rapides (aligné avec l'API)
 * Utilisé pour: GET /business-units/:id/stats
 */
export interface BusinessUnitQuickStats {
  childCount: number;               // Nombre total d'enfants
  activeChildCount: number;         // Nombre d'enfants actifs
  branchCount: number;              // Nombre de succursales
  posCount: number;                 // Nombre de points de vente
}

/**
 * DTO pour créer une Business Unit
 * Utilisé pour: POST /business-units
 */
export interface CreateBusinessUnitDto {
  code: string;
  name: string;
  type: BusinessUnitType;
  companyId: string;
  parentId?: string;
  address?: string;
  city?: string;
  province?: string;
  country?: string;
  phone?: string;
  email?: string;
  manager?: string;
  managerId?: string;
  currency?: string;
  timezone?: string;
  settings?: BusinessUnitSettings;
}

/**
 * DTO pour mettre à jour une Business Unit
 * Utilisé pour: PUT /business-units/:id
 */
export interface UpdateBusinessUnitDto {
  name?: string;
  address?: string;
  city?: string;
  province?: string;
  phone?: string;
  email?: string;
  manager?: string;
  managerId?: string;
  settings?: BusinessUnitSettings;
}

/**
 * Réponse paginée pour la liste des Business Units
 */
export interface PaginatedBusinessUnitsResponse {
  data: BusinessUnit[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

/**
 * Structure hiérarchique complète de l'organisation
 */
export interface OrganizationHierarchy {
  company: Company;
  branches: Branch[];
  pointsOfSale: PointOfSale[];
}

/**
 * Options pour le sélecteur d'unité commerciale
 */
export interface BusinessUnitOption {
  id: string;
  code: string;
  name: string;
  type: BusinessUnitType;
  level: number; // 0 pour company, 1 pour branch, 2 pour pos
  parentName?: string;
  fullPath?: string; // Ex: "Entreprise > Succursale Nord > Magasin Centre"
}

/**
 * Génère le chemin complet d'une unité commerciale
 */
export function getBusinessUnitPath(
  unit: BusinessUnit,
  hierarchy: OrganizationHierarchy
): string {
  if (unit.type === 'company') {
    return unit.name;
  }
  
  if (unit.type === 'branch') {
    return `${hierarchy.company.name} > ${unit.name}`;
  }
  
  if (unit.type === 'pos') {
    const branch = hierarchy.branches.find(b => b.id === (unit as PointOfSale).branchId);
    return branch 
      ? `${hierarchy.company.name} > ${branch.name} > ${unit.name}`
      : `${hierarchy.company.name} > ${unit.name}`;
  }
  
  return unit.name;
}

/**
 * Convertit la hiérarchie en liste plate d'options pour le sélecteur
 */
export function flattenHierarchyToOptions(
  hierarchy: OrganizationHierarchy
): BusinessUnitOption[] {
  const options: BusinessUnitOption[] = [];
  
  // Ajouter l'entreprise (niveau 0)
  options.push({
    id: hierarchy.company.id,
    code: hierarchy.company.code,
    name: hierarchy.company.name,
    type: 'company',
    level: 0,
    fullPath: hierarchy.company.name
  });
  
  // Ajouter les succursales (niveau 1)
  hierarchy.branches.forEach(branch => {
    options.push({
      id: branch.id,
      code: branch.code,
      name: branch.name,
      type: 'branch',
      level: 1,
      parentName: hierarchy.company.name,
      fullPath: `${hierarchy.company.name} > ${branch.name}`
    });
    
    // Ajouter les points de vente de cette succursale (niveau 2)
    const branchPOS = hierarchy.pointsOfSale.filter(pos => pos.branchId === branch.id);
    branchPOS.forEach(pos => {
      options.push({
        id: pos.id,
        code: pos.code,
        name: pos.name,
        type: 'pos',
        level: 2,
        parentName: branch.name,
        fullPath: `${hierarchy.company.name} > ${branch.name} > ${pos.name}`
      });
    });
  });
  
  return options;
}

/**
 * Récupère tous les IDs des unités enfants d'une unité donnée
 */
export function getChildUnitIds(
  unitId: string,
  unitType: BusinessUnitType,
  hierarchy: OrganizationHierarchy
): string[] {
  const ids: string[] = [unitId];
  
  if (unitType === 'company') {
    // Ajouter toutes les succursales et points de vente
    hierarchy.branches.forEach(branch => {
      ids.push(branch.id);
    });
    hierarchy.pointsOfSale.forEach(pos => {
      ids.push(pos.id);
    });
  } else if (unitType === 'branch') {
    // Ajouter tous les points de vente de cette succursale
    hierarchy.pointsOfSale
      .filter(pos => pos.branchId === unitId)
      .forEach(pos => {
        ids.push(pos.id);
      });
  }
  // Si c'est un point de vente, pas d'enfants
  
  return ids;
}
